import { isRight } from 'fp-ts/Either';
import {
  AlertCircle,
  CalendarRange,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Flag,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Smartphone,
  Target,
} from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import type { ListQuestCompletionsOutput } from '@src/dto/QuestWorkspace';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { QUEST_COMPLETION_API_URL } from '../../lib/config';
import { useTRPC } from '../../hooks/useTRPC';
import { getDappPathBySegment } from '../../utils/dappRoutes';
import { DappIcon, DappPickerDialog } from './DappPickerDialog';
import { QuestDappStorePreview } from './QuestDappStorePreview';
import { QuestPolarisPreview } from './QuestPolarisPreview';
import {
  appAdvancedFields,
  appGroups,
  credentialAdvancedFields,
  credentialGroups,
  credentialSigningFields,
  questAdvancedFields,
  questGroups,
  roundAdvancedFields,
  roundGroups,
  roundOptionsOf,
  seasonAdvancedFields,
  seasonGroups,
  selectionAdvancedFields,
  selectionGroups,
} from './QuestWorkspaceEditorConfig';
import {
  AdvancedSection,
  ChildrenPanel,
  EditorFrame,
  EditorSection,
  FieldGrid,
  FieldGroups,
  minimalButtonClassName,
  primaryActionButtonClassName,
  StatusBadge,
  TableSkeleton,
  type Crumb,
  type PatchHandler,
} from './QuestWorkspaceEditorLayout';
import { cascadeCounts, cascadeSummaryText } from './questWorkspaceLocalState';
import {
  appDisplayName,
  credentialDisplayName,
  defaultRoundClosingCopy,
  deriveRoundDateState,
  questDisplayName,
  questWorkspaceTargetKey,
  roundDisplayName,
  seasonDisplayName,
  statusTone,
  type QuestCredentialForm,
  type QuestForm,
  type QuestRoundAppForm,
  type QuestRoundForm,
  type QuestSeasonForm,
  type QuestSelectionPreviewForm,
  type QuestWorkspaceForm,
  type QuestWorkspaceItemRef,
  type RoundDateState,
  type StatusTone,
} from './questWorkspace';

type QuestCompletions = ListQuestCompletionsOutput;
type FieldErrors = Record<string, string | undefined>;

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isPersistedId(id: string) {
  return uuidPattern.test(id);
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatJson(value: string) {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

type SelectHandler = (ref: QuestWorkspaceItemRef) => void;

function DisableToggleButton(props: {
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(
        minimalButtonClassName,
        'min-w-[6rem] justify-center',
        props.disabled
          ? 'border-emerald-500/30 text-emerald-300 hover:border-emerald-400/60 hover:bg-emerald-500/10 hover:text-emerald-200'
          : 'border-rose-500/30 text-rose-300 hover:border-rose-400/60 hover:bg-rose-500/10 hover:text-rose-200',
      )}
      onClick={props.onToggle}
    >
      {props.disabled ? (
        <Eye className="size-4" />
      ) : (
        <EyeOff className="size-4" />
      )}
      {props.disabled ? 'Enable' : 'Disable'}
    </Button>
  );
}

function UnrevealedToggleButton(props: {
  unrevealed: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(
        minimalButtonClassName,
        'min-w-[6rem] justify-center',
        props.unrevealed
          ? 'border-sky-500/30 text-sky-300 hover:border-sky-400/60 hover:bg-sky-500/10 hover:text-sky-200'
          : 'border-amber-500/30 text-amber-300 hover:border-amber-400/60 hover:bg-amber-500/10 hover:text-amber-200',
      )}
      onClick={props.onToggle}
    >
      {props.unrevealed ? (
        <Eye className="size-4" />
      ) : (
        <EyeOff className="size-4" />
      )}
      {props.unrevealed ? 'Reveal' : 'Unreveal'}
    </Button>
  );
}

const roundDateStateTone: Record<RoundDateState, StatusTone> = {
  Unknown: 'neutral',
  Scheduled: 'info',
  Open: 'success',
  Finalizing: 'warn',
  Expired: 'warn',
  Closed: 'neutral',
};

function RoundDateStateBadge(props: { state: RoundDateState }) {
  return (
    <StatusBadge
      tone={roundDateStateTone[props.state]}
      aria-label={`Round state: ${props.state}`}
    >
      State: {props.state}
    </StatusBadge>
  );
}

function parseDateMillis(value: string) {
  const parsed = value.trim() ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

function questBadgeCountState(options: {
  quest: QuestForm;
  app?: QuestRoundAppForm;
  round?: QuestRoundForm;
}): { tone: StatusTone; label: string; title: string } {
  if (
    options.quest.disabled ||
    options.quest.status !== 'Active' ||
    options.app?.disabled ||
    options.round?.disabled
  ) {
    return {
      tone: 'danger',
      label: 'No badge',
      title:
        'Completion requests do not count while this quest, app, or round is disabled.',
    };
  }

  if (options.app?.unrevealed) {
    return {
      tone: 'warn',
      label: 'Recorded only',
      title:
        'The app is unrevealed, so requests are recorded but badges are not awarded.',
    };
  }

  const endsAt = options.round ? parseDateMillis(options.round.endsAt) : null;
  if (endsAt === null) {
    return {
      tone: 'neutral',
      label: 'Badge unknown',
      title: 'Set a valid round end time to determine badge counting.',
    };
  }

  if (Date.now() > endsAt) {
    return {
      tone: 'warn',
      label: 'Recorded only',
      title:
        'The round has ended, so requests are recorded but badges are not awarded.',
    };
  }

  return {
    tone: 'success',
    label: 'Counts for badges',
    title: 'Completion requests received now will award badges.',
  };
}

function QuestBadgeCountBadge(props: {
  quest: QuestForm;
  app?: QuestRoundAppForm;
  round?: QuestRoundForm;
}) {
  const state = questBadgeCountState(props);

  return (
    <StatusBadge tone={state.tone} title={state.title}>
      {state.label}
    </StatusBadge>
  );
}

function seasonCrumb(
  workspace: QuestWorkspaceForm,
  seasonId: string,
  onSelect: SelectHandler,
): Crumb | null {
  const index = workspace.seasons.findIndex((season) => season.id === seasonId);
  const season = workspace.seasons[index];

  return season
    ? {
        key: questWorkspaceTargetKey({ kind: 'season', id: season.id }),
        label: seasonDisplayName(season, index),
        onClick: () => onSelect({ kind: 'season', id: season.id }),
      }
    : null;
}

function roundCrumb(
  workspace: QuestWorkspaceForm,
  roundId: string,
  onSelect: SelectHandler,
): Crumb | null {
  const index = workspace.rounds.findIndex((round) => round.id === roundId);
  const round = workspace.rounds[index];

  return round
    ? {
        key: questWorkspaceTargetKey({ kind: 'round', id: round.id }),
        label: roundDisplayName(round, index),
        onClick: () => onSelect({ kind: 'round', id: round.id }),
      }
    : null;
}

function appCrumb(
  workspace: QuestWorkspaceForm,
  appId: string,
  onSelect: SelectHandler,
): Crumb | null {
  const index = workspace.apps.findIndex((app) => app.id === appId);
  const app = workspace.apps[index];

  return app
    ? {
        key: questWorkspaceTargetKey({ kind: 'app', id: app.id }),
        label: appDisplayName(app, index),
        onClick: () => onSelect({ kind: 'app', id: app.id }),
      }
    : null;
}

function QuestCompletionEndpoint(props: {
  keyPrefix: string;
  generatedApiKey?: string | null;
}) {
  const [showApiKey, setShowApiKey] = useState(false);
  const apiKeyHint = props.keyPrefix
    ? showApiKey && props.generatedApiKey
      ? props.generatedApiKey
      : `${props.keyPrefix}.<secret>`
    : '<generated on save>';

  const copyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(QUEST_COMPLETION_API_URL);
      toast.success('Endpoint copied to clipboard');
    } catch {
      toast.error('Could not copy the endpoint');
    }
  };

  const copyApiKey = async () => {
    if (!props.generatedApiKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(props.generatedApiKey);
      toast.success('API key copied to clipboard');
    } catch {
      toast.error('Could not copy the API key');
    }
  };

  return (
    <EditorSection
      title="Quest completion API"
      description="Partners holding this credential submit completions to this endpoint."
      accent
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="shrink-0 rounded-md border border-teal-500/30 bg-teal-500/10 px-1.5 py-1 font-mono text-[11px] font-semibold text-teal-300">
            POST
          </span>
          <code className="min-w-0 flex-1 break-all rounded-lg border border-neutral-800 bg-neutral-950/60 px-3 py-2 font-mono text-xs leading-relaxed text-neutral-200">
            {QUEST_COMPLETION_API_URL}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={minimalButtonClassName}
            onClick={copyEndpoint}
          >
            <Copy className="size-4" />
            Copy
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs leading-relaxed text-neutral-400">
          <span>Authenticate with</span>
          <code className="rounded bg-neutral-800/80 px-1 py-0.5 font-mono text-[11px] text-neutral-300">
            x-api-key: {apiKeyHint}
          </code>
          {props.generatedApiKey && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(minimalButtonClassName, 'h-7 px-2')}
                onClick={() => setShowApiKey((current) => !current)}
              >
                {showApiKey ? (
                  <EyeOff className="size-3.5" />
                ) : (
                  <Eye className="size-3.5" />
                )}
                {showApiKey ? 'Hide key' : 'Show key'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(minimalButtonClassName, 'h-7 px-2')}
                onClick={copyApiKey}
              >
                <Copy className="size-3.5" />
                Copy key
              </Button>
            </>
          )}
        </div>
      </div>
    </EditorSection>
  );
}

function SummaryStat(props: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: number;
}) {
  const Icon = props.icon;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-neutral-800/60 bg-neutral-900/30 px-4 py-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-800/60 text-teal-300">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-semibold text-white tabular-nums">
          {props.value}
        </div>
        <div className="truncate text-xs text-neutral-400">{props.label}</div>
      </div>
    </div>
  );
}

function OverviewEditor(props: {
  workspace: QuestWorkspaceForm;
  issues: { message: string; target: QuestWorkspaceItemRef }[];
  onSelect: SelectHandler;
}) {
  const { workspace } = props;
  const disabledCount =
    workspace.rounds.filter((round) => round.disabled).length +
    workspace.apps.filter((app) => app.disabled).length +
    workspace.quests.filter((quest) => quest.disabled).length;
  const unrevealedCount = workspace.apps.filter((app) => app.unrevealed).length;

  const statusBreakdown = new Map<string, number>();
  for (const round of workspace.rounds) {
    statusBreakdown.set(
      round.status,
      (statusBreakdown.get(round.status) ?? 0) + 1,
    );
  }

  const previewRound = workspace.rounds.find(
    (round) => round.id === workspace.selectionPreview.selectedRoundId,
  );
  const previewRoundIndex = workspace.rounds.findIndex(
    (round) => round.id === workspace.selectionPreview.selectedRoundId,
  );

  const issueTargets = new Map<
    string,
    { ref: QuestWorkspaceItemRef; count: number }
  >();
  for (const issue of props.issues) {
    if (issue.target.kind === 'overview' || issue.target.kind === 'selection') {
      continue;
    }
    const key = questWorkspaceTargetKey(issue.target);
    const existing = issueTargets.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      issueTargets.set(key, { ref: issue.target, count: 1 });
    }
  }

  const labelForRef = (ref: QuestWorkspaceItemRef): string => {
    if (ref.kind === 'season') {
      const i = workspace.seasons.findIndex((s) => s.id === ref.id);
      return i >= 0
        ? `Season · ${seasonDisplayName(workspace.seasons[i], i)}`
        : 'Season';
    }
    if (ref.kind === 'round') {
      const i = workspace.rounds.findIndex((r) => r.id === ref.id);
      return i >= 0
        ? `Round · ${roundDisplayName(workspace.rounds[i], i)}`
        : 'Round';
    }
    if (ref.kind === 'app') {
      const i = workspace.apps.findIndex((a) => a.id === ref.id);
      return i >= 0 ? `App · ${appDisplayName(workspace.apps[i], i)}` : 'App';
    }
    if (ref.kind === 'quest') {
      const i = workspace.quests.findIndex((q) => q.id === ref.id);
      return i >= 0
        ? `Quest · ${questDisplayName(workspace.quests[i], i)}`
        : 'Quest';
    }
    if (ref.kind === 'credential') {
      const i = workspace.credentials.findIndex((c) => c.id === ref.id);
      return i >= 0
        ? `API key · ${credentialDisplayName(workspace.credentials[i], i)}`
        : 'API key';
    }
    return 'Item';
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2.5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-neutral-800 bg-neutral-900/60 text-teal-300">
          <LayoutDashboard className="size-4" />
        </span>
        <h2
          data-editor-heading
          tabIndex={-1}
          className="text-lg font-semibold text-white outline-none"
        >
          Overview
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <SummaryStat
          icon={CalendarRange}
          label="Seasons"
          value={workspace.seasons.length}
        />
        <SummaryStat
          icon={Flag}
          label="Rounds"
          value={workspace.rounds.length}
        />
        <SummaryStat
          icon={Smartphone}
          label="Apps"
          value={workspace.apps.length}
        />
        <SummaryStat
          icon={Target}
          label="Quests"
          value={workspace.quests.length}
        />
        <SummaryStat
          icon={KeyRound}
          label="API keys"
          value={workspace.credentials.length}
        />
      </div>

      <EditorSection title="Round status">
        <div className="flex flex-wrap gap-2">
          {statusBreakdown.size === 0 ? (
            <span className="text-xs text-neutral-500">No rounds yet.</span>
          ) : (
            [...statusBreakdown.entries()].map(([status, count]) => (
              <StatusBadge
                key={status}
                tone={statusTone(status)}
                className="px-2.5"
              >
                {status}
                <span className="tabular-nums opacity-80">{count}</span>
              </StatusBadge>
            ))
          )}
          {disabledCount > 0 && (
            <StatusBadge tone="danger" className="px-2.5">
              Disabled
              <span className="tabular-nums opacity-80">{disabledCount}</span>
            </StatusBadge>
          )}
          {unrevealedCount > 0 && (
            <StatusBadge tone="warn" className="px-2.5">
              Unrevealed
              <span className="tabular-nums opacity-80">{unrevealedCount}</span>
            </StatusBadge>
          )}
        </div>
      </EditorSection>

      <EditorSection title="Airdrop selection">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-neutral-300">
            {previewRound
              ? `Targeting ${roundDisplayName(previewRound, previewRoundIndex)}`
              : 'No round targeted for selection preview.'}
          </span>
          <button
            type="button"
            onClick={() => props.onSelect({ kind: 'selection' })}
            className="shrink-0 text-sm font-medium text-teal-300 transition-colors hover:text-teal-200"
          >
            Open selection →
          </button>
        </div>
      </EditorSection>

      <div className="grid gap-5 xl:grid-cols-2">
        <QuestPolarisPreview />
        <QuestDappStorePreview />
      </div>

      {/* Kept last: this is the only variable-height section, so changes to the
          issue list never push other content. */}
      <EditorSection
        title={
          issueTargets.size === 0
            ? 'Validation'
            : `Needs attention · ${issueTargets.size}`
        }
        description={
          issueTargets.size === 0
            ? undefined
            : 'Click an item to jump to it and fix its fields.'
        }
      >
        {issueTargets.size === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-300">
            <CheckCircle2 className="size-4" />
            Everything looks valid.
          </div>
        ) : (
          <ul className="space-y-1">
            {[...issueTargets.values()].map(({ ref, count }) => (
              <li key={questWorkspaceTargetKey(ref)}>
                <button
                  type="button"
                  onClick={() => props.onSelect(ref)}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border border-neutral-800/60 bg-neutral-900/40 px-3 py-2 text-left text-sm text-neutral-200 transition-colors hover:border-rose-500/40 hover:bg-rose-950/20"
                >
                  <span className="min-w-0 truncate">{labelForRef(ref)}</span>
                  <span className="inline-flex shrink-0 items-center gap-1 text-rose-300">
                    <AlertCircle className="size-3.5" />
                    <span className="text-xs font-semibold tabular-nums">
                      {count}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </EditorSection>
    </div>
  );
}

function SeasonEditor(props: {
  workspace: QuestWorkspaceForm;
  seasonId: string;
  errors: FieldErrors;
  onSelect: SelectHandler;
  onPatch: PatchHandler;
  onAddRound: (seasonId: string) => void;
  onRemove: () => void;
}) {
  const { workspace } = props;
  const index = workspace.seasons.findIndex(
    (season) => season.id === props.seasonId,
  );
  const season = workspace.seasons[index];

  if (!season) {
    return null;
  }

  const onChange = (key: keyof QuestSeasonForm & string, value: string) =>
    props.onPatch('seasons', season.id, { [key]: value });

  return (
    <EditorFrame
      title={seasonDisplayName(season, index)}
      kindLabel="Season"
      icon={<CalendarRange className="size-4" />}
      onRemove={props.onRemove}
      removeCascadeSummary={cascadeSummaryText(
        cascadeCounts(workspace, { kind: 'season', id: season.id }),
      )}
    >
      <FieldGroups
        item={season}
        groups={seasonGroups}
        idPrefix={`season-${season.id}`}
        onChange={onChange}
        errors={props.errors}
      />
      <AdvancedSection>
        <FieldGrid
          item={season}
          fields={seasonAdvancedFields}
          idPrefix={`season-${season.id}`}
          onChange={onChange}
          errors={props.errors}
        />
      </AdvancedSection>
      <ChildrenPanel
        title="Rounds"
        addLabel="Add round"
        icon={Flag}
        items={workspace.rounds
          .map((round, roundIndex) =>
            round.seasonId === season.id
              ? { id: round.id, label: roundDisplayName(round, roundIndex) }
              : null,
          )
          .filter((item) => item !== null)}
        onOpen={(id) => props.onSelect({ kind: 'round', id })}
        onAdd={() => props.onAddRound(season.id)}
      />
    </EditorFrame>
  );
}

function RoundEditor(props: {
  workspace: QuestWorkspaceForm;
  roundId: string;
  errors: FieldErrors;
  onSelect: SelectHandler;
  onPatch: PatchHandler;
  onAddApp: (roundId: string) => void;
  onPreviewRound: (roundId: string) => void;
  onRemove: () => void;
}) {
  const { workspace } = props;
  const index = workspace.rounds.findIndex(
    (round) => round.id === props.roundId,
  );
  const round = workspace.rounds[index];

  if (!round) {
    return null;
  }

  const breadcrumb = [
    seasonCrumb(workspace, round.seasonId, props.onSelect),
  ].filter((crumb) => crumb !== null);
  const onChange = (key: keyof QuestRoundForm & string, value: unknown) =>
    props.onPatch('rounds', round.id, { [key]: value });
  const applyClosingDefault = () =>
    props.onPatch('rounds', round.id, defaultRoundClosingCopy(round));

  return (
    <EditorFrame
      breadcrumb={breadcrumb}
      title={roundDisplayName(round, index)}
      kindLabel="Round"
      icon={<Flag className="size-4" />}
      status={round.status}
      extraBadges={<RoundDateStateBadge state={deriveRoundDateState(round)} />}
      disabled={round.disabled}
      primaryAction={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={primaryActionButtonClassName}
          onClick={() => props.onPreviewRound(round.id)}
        >
          <Play className="size-4" />
          Run selection preview
        </Button>
      }
      actions={
        <DisableToggleButton
          disabled={round.disabled}
          onToggle={() => onChange('disabled', !round.disabled)}
        />
      }
      onRemove={props.onRemove}
      removeCascadeSummary={cascadeSummaryText(
        cascadeCounts(workspace, { kind: 'round', id: round.id }),
      )}
    >
      <FieldGroups
        item={round}
        groups={roundGroups()}
        idPrefix={`round-${round.id}`}
        onChange={onChange}
        errors={props.errors}
        fieldActions={{
          closingBlurb: (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(minimalButtonClassName, 'h-7 px-2 text-xs')}
              onClick={applyClosingDefault}
            >
              <RefreshCw className="size-3.5" />
              Apply default
            </Button>
          ),
        }}
      />
      <AdvancedSection description="Eligibility overrides, JSON, and metadata">
        <FieldGrid
          item={round}
          fields={roundAdvancedFields}
          idPrefix={`round-${round.id}`}
          onChange={onChange}
          errors={props.errors}
        />
      </AdvancedSection>
      <ChildrenPanel
        title="Apps"
        addLabel="Add app"
        icon={Smartphone}
        items={workspace.apps
          .map((app, appIndex) =>
            app.roundId === round.id
              ? {
                  id: app.id,
                  label: appDisplayName(app, appIndex),
                  iconUrl: app.iconUrl || undefined,
                }
              : null,
          )
          .filter((item) => item !== null)}
        onOpen={(id) => props.onSelect({ kind: 'app', id })}
        onAdd={() => props.onAddApp(round.id)}
      />
    </EditorFrame>
  );
}

function AppEditor(props: {
  workspace: QuestWorkspaceForm;
  appId: string;
  errors: FieldErrors;
  onSelect: SelectHandler;
  onPatch: PatchHandler;
  onAddQuest: (appId: string) => void;
  onAddCredential: (appId: string) => void;
  onRemove: () => void;
}) {
  const { workspace } = props;
  const trpc = useTRPC();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [isFillingCurationNote, setIsFillingCurationNote] = useState(false);
  const index = workspace.apps.findIndex((app) => app.id === props.appId);
  const app = workspace.apps[index];

  if (!app) {
    return null;
  }

  const round = workspace.rounds.find((item) => item.id === app.roundId);
  const title = appDisplayName(app, index);
  const appPortalPath = app.androidPackage.trim()
    ? getDappPathBySegment(app.androidPackage.trim())
    : null;
  const breadcrumb = [
    round ? seasonCrumb(workspace, round.seasonId, props.onSelect) : null,
    roundCrumb(workspace, app.roundId, props.onSelect),
  ].filter((crumb) => crumb !== null);
  const onChange = (key: keyof QuestRoundAppForm & string, value: unknown) =>
    props.onPatch('apps', app.id, { [key]: value });
  const fillCurationNoteFromSubtitle = async () => {
    const packageName = app.androidPackage.trim().toLowerCase();
    const appName = app.appName.trim().toLowerCase();
    const search = packageName || appName;
    if (!search) {
      toast.error('Add an app package or name first.');
      return;
    }

    setIsFillingCurationNote(true);
    try {
      const result = await trpc.listAllDapps.query({
        filters: { search },
        pagination: { page: 1, limit: 8 },
      });
      if (!isRight(result)) {
        toast.error(result.left.message || 'Could not load app details.');
        return;
      }

      const catalogApp =
        result.right.dapps.find(
          (dapp) => dapp.packageName?.toLowerCase() === packageName,
        ) ??
        result.right.dapps.find(
          (dapp) => dapp.dappName.toLowerCase() === appName,
        );
      const subtitle = catalogApp?.subtitle?.trim();
      if (!subtitle) {
        toast.error('No subtitle found for this app.');
        return;
      }

      props.onPatch('apps', app.id, { curationNote: subtitle });
      toast.success('Curation note filled from app subtitle');
    } catch {
      toast.error('Could not load app details.');
    } finally {
      setIsFillingCurationNote(false);
    }
  };

  return (
    <EditorFrame
      breadcrumb={breadcrumb}
      title={
        appPortalPath ? (
          <Link
            to={appPortalPath}
            className="rounded-sm transition-colors hover:text-teal-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
          >
            {title}
          </Link>
        ) : (
          title
        )
      }
      kindLabel="App"
      icon={
        app.iconUrl ? (
          <DappIcon url={app.iconUrl} name={title} className="size-9 text-sm" />
        ) : (
          <Smartphone className="size-4" />
        )
      }
      primaryAction={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={primaryActionButtonClassName}
          onClick={() => setCatalogOpen(true)}
        >
          <Search className="size-4" />
          Fill from catalog
        </Button>
      }
      actions={
        <>
          <DisableToggleButton
            disabled={app.disabled}
            onToggle={() => onChange('disabled', !app.disabled)}
          />
          <UnrevealedToggleButton
            unrevealed={app.unrevealed}
            onToggle={() => onChange('unrevealed', !app.unrevealed)}
          />
        </>
      }
      disabled={app.disabled}
      inheritedDisabled={round?.disabled}
      onRemove={props.onRemove}
      removeCascadeSummary={cascadeSummaryText(
        cascadeCounts(workspace, { kind: 'app', id: app.id }),
      )}
    >
      {catalogOpen && (
        <DappPickerDialog
          title="Fill from catalog"
          description="Pick a dapp from the portal catalog to prefill this app's name, package, and icon."
          onClose={() => setCatalogOpen(false)}
          onPick={(patch) => {
            props.onPatch('apps', app.id, patch);
            setCatalogOpen(false);
          }}
        />
      )}
      <FieldGroups
        item={app}
        groups={appGroups(roundOptionsOf(workspace))}
        idPrefix={`app-${app.id}`}
        onChange={onChange}
        errors={props.errors}
        fieldActions={{
          curationNote: (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(minimalButtonClassName, 'h-7 px-2 text-xs')}
              disabled={isFillingCurationNote}
              onClick={fillCurationNoteFromSubtitle}
            >
              {isFillingCurationNote ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Copy className="size-3.5" />
              )}
              Fill from subtitle
            </Button>
          ),
        }}
      />
      <AdvancedSection>
        <FieldGrid
          item={app}
          fields={appAdvancedFields}
          idPrefix={`app-${app.id}`}
          onChange={onChange}
          errors={props.errors}
        />
      </AdvancedSection>
      <ChildrenPanel
        title="Quests"
        addLabel="Add quest"
        icon={Target}
        items={workspace.quests
          .map((quest, questIndex) =>
            quest.roundAppId === app.id
              ? { id: quest.id, label: questDisplayName(quest, questIndex) }
              : null,
          )
          .filter((item) => item !== null)}
        onOpen={(id) => props.onSelect({ kind: 'quest', id })}
        onAdd={() => props.onAddQuest(app.id)}
      />
      <ChildrenPanel
        title="API keys"
        addLabel="Add API key"
        icon={KeyRound}
        items={workspace.credentials
          .map((credential, credentialIndex) =>
            credential.scopedAppIds.includes(app.id)
              ? {
                  id: credential.id,
                  label: credentialDisplayName(credential, credentialIndex),
                }
              : null,
          )
          .filter((item) => item !== null)}
        onOpen={(id) => props.onSelect({ kind: 'credential', id })}
        onAdd={() => props.onAddCredential(app.id)}
      />
    </EditorFrame>
  );
}

function QuestCompletionRequestsPanel(props: {
  completions: QuestCompletions | null;
  error: string | null;
  isLoading: boolean;
  isPersisted: boolean;
}) {
  let body: ReactNode;

  if (!props.isPersisted) {
    body = (
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100/80">
        Save this quest before loading completion requests.
      </div>
    );
  } else if (props.isLoading) {
    body = <TableSkeleton rows={4} columns={5} />;
  } else if (props.error) {
    body = (
      <div
        role="alert"
        className="rounded-lg border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-200"
      >
        {props.error}
      </div>
    );
  } else {
    const completions = props.completions?.completions ?? [];
    body =
      completions.length === 0 ? (
        <div className="rounded-lg border border-neutral-800 bg-neutral-950/40 px-3 py-2 text-sm text-neutral-500">
          No completion requests recorded.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-800 text-left text-xs">
            <thead className="font-medium text-neutral-400">
              <tr>
                <th className="px-3 py-2">Wallet</th>
                <th className="px-3 py-2">Request</th>
                <th className="px-3 py-2">Credential</th>
                <th className="px-3 py-2">Completed</th>
                <th className="px-3 py-2">Metadata</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800/70 text-neutral-300">
              {completions.map((completion) => (
                <tr key={completion.id} className="hover:bg-neutral-800/30">
                  <td className="max-w-52 px-3 py-2 align-top">
                    <div className="break-all font-mono">
                      {completion.walletId}
                    </div>
                  </td>
                  <td className="max-w-44 px-3 py-2 align-top">
                    <div className="break-all font-mono text-neutral-400">
                      {completion.sourceRequestId || '-'}
                    </div>
                  </td>
                  <td className="max-w-44 px-3 py-2 align-top">
                    <div className="truncate text-[13px] text-neutral-200">
                      {completion.credentialLabel || '-'}
                    </div>
                    {completion.credentialKeyPrefix && (
                      <div className="mt-0.5 break-all font-mono text-neutral-500">
                        {completion.credentialKeyPrefix}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 align-top text-neutral-400">
                    {formatDateTime(completion.completedAt)}
                  </td>
                  <td className="min-w-56 px-3 py-2 align-top">
                    <pre className="max-h-28 overflow-auto whitespace-pre-wrap rounded-md border border-neutral-800 bg-neutral-950/60 p-2 font-mono text-[11px] leading-relaxed text-neutral-400">
                      {formatJson(completion.metadataJson)}
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }

  const totalCount = props.completions?.totalCount ?? 0;
  const shown = props.completions?.completions.length ?? 0;

  return (
    <EditorSection
      title={
        props.isPersisted && !props.isLoading && !props.error
          ? `Completion requests (${totalCount})`
          : 'Completion requests'
      }
      description={totalCount > shown ? `Showing latest ${shown}` : undefined}
    >
      <div className="min-h-[5rem]">{body}</div>
    </EditorSection>
  );
}

function QuestEditor(props: {
  workspace: QuestWorkspaceForm;
  questId: string;
  errors: FieldErrors;
  onSelect: SelectHandler;
  onPatch: PatchHandler;
  onRemove: () => void;
  onLoadQuestCompletions: (questId: string) => Promise<QuestCompletions>;
}) {
  const { workspace } = props;
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [completions, setCompletions] = useState<QuestCompletions | null>(null);
  const [completionsError, setCompletionsError] = useState<string | null>(null);
  const [isLoadingCompletions, setIsLoadingCompletions] = useState(false);
  const index = workspace.quests.findIndex(
    (quest) => quest.id === props.questId,
  );
  const workspaceQuest = workspace.quests[index];
  const quest = workspaceQuest
    ? { ...workspaceQuest, badgeTitle: workspaceQuest.title }
    : undefined;

  if (!quest) {
    return null;
  }

  const app = workspace.apps.find((item) => item.id === quest.roundAppId);
  const round = app
    ? workspace.rounds.find((item) => item.id === app.roundId)
    : undefined;
  const breadcrumb = [
    round ? seasonCrumb(workspace, round.seasonId, props.onSelect) : null,
    app ? roundCrumb(workspace, app.roundId, props.onSelect) : null,
    appCrumb(workspace, quest.roundAppId, props.onSelect),
  ].filter((crumb) => crumb !== null);
  const onChange = (key: keyof QuestForm & string, value: unknown) =>
    props.onPatch('quests', quest.id, { [key]: value });
  const persistedQuest = isPersistedId(quest.id);
  const toggleRequests = async () => {
    if (requestsOpen) {
      setRequestsOpen(false);
      return;
    }

    setRequestsOpen(true);
    if (!persistedQuest || isLoadingCompletions) {
      return;
    }

    setIsLoadingCompletions(true);
    setCompletionsError(null);
    try {
      setCompletions(await props.onLoadQuestCompletions(quest.id));
    } catch (error) {
      setCompletionsError(
        error instanceof Error
          ? error.message
          : 'Could not load quest requests.',
      );
    } finally {
      setIsLoadingCompletions(false);
    }
  };

  return (
    <EditorFrame
      breadcrumb={breadcrumb}
      title={questDisplayName(quest, index)}
      kindLabel="Quest"
      icon={<Target className="size-4" />}
      status={quest.status}
      extraBadges={
        <QuestBadgeCountBadge quest={quest} app={app} round={round} />
      }
      disabled={quest.disabled}
      inheritedDisabled={round?.disabled || app?.disabled}
      primaryAction={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            primaryActionButtonClassName,
            'min-w-[7.5rem] justify-center',
          )}
          disabled={isLoadingCompletions}
          onClick={() => void toggleRequests()}
        >
          <span className="flex size-4 items-center justify-center">
            {isLoadingCompletions ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <ListChecks className="size-4" />
            )}
          </span>
          {requestsOpen ? 'Hide requests' : 'View requests'}
        </Button>
      }
      actions={
        <DisableToggleButton
          disabled={quest.disabled}
          onToggle={() => onChange('disabled', !quest.disabled)}
        />
      }
      onRemove={props.onRemove}
    >
      <FieldGroups
        item={quest}
        groups={questGroups()}
        idPrefix={`quest-${quest.id}`}
        onChange={onChange}
        errors={props.errors}
      />
      <AdvancedSection>
        <FieldGrid
          item={quest}
          fields={questAdvancedFields}
          idPrefix={`quest-${quest.id}`}
          onChange={onChange}
          errors={props.errors}
        />
      </AdvancedSection>
      {requestsOpen && (
        <QuestCompletionRequestsPanel
          completions={completions}
          error={completionsError}
          isLoading={isLoadingCompletions}
          isPersisted={persistedQuest}
        />
      )}
    </EditorFrame>
  );
}

function CredentialEditor(props: {
  workspace: QuestWorkspaceForm;
  credentialId: string;
  errors: FieldErrors;
  generatedCredentialApiKeys: ReadonlyMap<string, string>;
  onSelect: SelectHandler;
  onPatch: PatchHandler;
  onRemove: () => void;
}) {
  const { workspace } = props;
  const index = workspace.credentials.findIndex(
    (credential) => credential.id === props.credentialId,
  );
  const credential = workspace.credentials[index];

  if (!credential) {
    return null;
  }

  const scopedApps = workspace.apps.filter((app) =>
    credential.scopedAppIds.includes(app.id),
  );
  const primaryApp = scopedApps[0];
  const round = primaryApp
    ? workspace.rounds.find((item) => item.id === primaryApp.roundId)
    : undefined;
  const breadcrumb = [
    round ? seasonCrumb(workspace, round.seasonId, props.onSelect) : null,
    primaryApp
      ? roundCrumb(workspace, primaryApp.roundId, props.onSelect)
      : null,
    primaryApp ? appCrumb(workspace, primaryApp.id, props.onSelect) : null,
  ].filter((crumb) => crumb !== null);

  const onChange = (key: keyof QuestCredentialForm & string, value: string) =>
    props.onPatch('credentials', credential.id, { [key]: value });

  return (
    <EditorFrame
      breadcrumb={breadcrumb}
      title={credentialDisplayName(credential, index)}
      kindLabel="API key"
      icon={<KeyRound className="size-4" />}
      status={credential.status}
      onRemove={props.onRemove}
    >
      <QuestCompletionEndpoint
        keyPrefix={credential.keyPrefix}
        generatedApiKey={
          props.generatedCredentialApiKeys.get(credential.id) ?? null
        }
      />
      {scopedApps.length > 0 && (
        <p className="px-1 text-xs text-neutral-400">
          Used by{' '}
          {scopedApps
            .map((app, appIndex) => appDisplayName(app, appIndex))
            .join(', ')}
          .
        </p>
      )}
      <FieldGroups
        item={credential}
        groups={credentialGroups}
        idPrefix={`credential-${credential.id}`}
        onChange={onChange}
        errors={props.errors}
      />
      <AdvancedSection
        title="Signing"
        description="Optional request signatures"
      >
        <FieldGrid
          item={credential}
          fields={credentialSigningFields}
          idPrefix={`credential-${credential.id}`}
          onChange={onChange}
          errors={props.errors}
        />
      </AdvancedSection>
      <AdvancedSection>
        <FieldGrid
          item={credential}
          fields={credentialAdvancedFields}
          idPrefix={`credential-${credential.id}`}
          onChange={onChange}
          errors={props.errors}
        />
      </AdvancedSection>
    </EditorFrame>
  );
}

function SelectionEditor(props: {
  workspace: QuestWorkspaceForm;
  selectionPreview: QuestSelectionPreviewForm;
  errors: FieldErrors;
  onPatchSelection: (patch: Partial<QuestSelectionPreviewForm>) => void;
  onRunSelectionPreview: () => void;
  onRevealAllocations: () => void;
  isRunningSelectionPreview: boolean;
  isRevealingAllocations: boolean;
}) {
  const onChange = (
    key: keyof QuestSelectionPreviewForm & string,
    value: string,
  ) => props.onPatchSelection({ [key]: value });

  // Guarantee the round <select> always has an option matching the stored
  // value, so a removed/stale selectedRoundId can't silently display a
  // different round than the one actually targeted.
  const roundOptions = roundOptionsOf(props.workspace);
  const selectedRoundId = props.selectionPreview.selectedRoundId;
  const hasSelectedRoundOption =
    selectedRoundId === '' ||
    roundOptions.some((option) => option.value === selectedRoundId);
  const augmentedRoundOptions = hasSelectedRoundOption
    ? roundOptions
    : [
        { value: selectedRoundId, label: 'Unknown round (removed)' },
        ...roundOptions,
      ];

  return (
    <EditorFrame
      title="Selection preview"
      kindLabel="Selection"
      icon={<ListChecks className="size-4" />}
    >
      <EditorSection
        title="Run controls"
        description="Create a backend preview run, then reveal allocations from the latest completed preview."
        accent
      >
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              minimalButtonClassName,
              'min-w-[11.5rem] justify-center',
            )}
            disabled={props.isRunningSelectionPreview}
            onClick={props.onRunSelectionPreview}
          >
            {props.isRunningSelectionPreview ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Play className="size-4" />
            )}
            {props.isRunningSelectionPreview
              ? 'Running preview'
              : 'Run selection preview'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              minimalButtonClassName,
              'min-w-[10.5rem] justify-center',
            )}
            disabled={props.isRevealingAllocations}
            onClick={props.onRevealAllocations}
          >
            {props.isRevealingAllocations ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Eye className="size-4" />
            )}
            {props.isRevealingAllocations
              ? 'Revealing allocations'
              : 'Reveal allocations'}
          </Button>
        </div>
      </EditorSection>
      <FieldGroups
        item={props.selectionPreview}
        groups={selectionGroups(augmentedRoundOptions)}
        idPrefix="selection"
        onChange={onChange}
        errors={props.errors}
      />
      <AdvancedSection>
        <FieldGrid
          item={props.selectionPreview}
          fields={selectionAdvancedFields}
          idPrefix="selection"
          onChange={onChange}
          errors={props.errors}
        />
      </AdvancedSection>
    </EditorFrame>
  );
}

export function QuestWorkspaceEditor(props: {
  workspace: QuestWorkspaceForm;
  selected: QuestWorkspaceItemRef;
  itemErrors: FieldErrors;
  issues: { message: string; target: QuestWorkspaceItemRef }[];
  generatedCredentialApiKeys: ReadonlyMap<string, string>;
  onSelect: SelectHandler;
  onPatch: PatchHandler;
  onPatchSelection: (patch: Partial<QuestSelectionPreviewForm>) => void;
  onRemove: (ref: QuestWorkspaceItemRef) => void;
  onAddRound: (seasonId: string) => void;
  onAddApp: (roundId: string) => void;
  onAddQuest: (appId: string) => void;
  onAddCredential: (appId: string) => void;
  onPreviewRound: (roundId: string) => void;
  onLoadQuestCompletions: (questId: string) => Promise<QuestCompletions>;
  onRunSelectionPreview: () => void;
  onRevealAllocations: () => void;
  isRunningSelectionPreview: boolean;
  isRevealingAllocations: boolean;
}) {
  const { workspace, selected } = props;
  const removeSelected = () => props.onRemove(selected);

  return (
    <div className="min-w-0">
      {selected.kind === 'overview' && (
        <OverviewEditor
          workspace={workspace}
          issues={props.issues}
          onSelect={props.onSelect}
        />
      )}
      {selected.kind === 'season' && (
        <SeasonEditor
          key={selected.id}
          workspace={workspace}
          seasonId={selected.id}
          errors={props.itemErrors}
          onSelect={props.onSelect}
          onPatch={props.onPatch}
          onAddRound={props.onAddRound}
          onRemove={removeSelected}
        />
      )}
      {selected.kind === 'round' && (
        <RoundEditor
          key={selected.id}
          workspace={workspace}
          roundId={selected.id}
          errors={props.itemErrors}
          onSelect={props.onSelect}
          onPatch={props.onPatch}
          onAddApp={props.onAddApp}
          onPreviewRound={props.onPreviewRound}
          onRemove={removeSelected}
        />
      )}
      {selected.kind === 'app' && (
        <AppEditor
          key={selected.id}
          workspace={workspace}
          appId={selected.id}
          errors={props.itemErrors}
          onSelect={props.onSelect}
          onPatch={props.onPatch}
          onAddQuest={props.onAddQuest}
          onAddCredential={props.onAddCredential}
          onRemove={removeSelected}
        />
      )}
      {selected.kind === 'quest' && (
        <QuestEditor
          key={selected.id}
          workspace={workspace}
          questId={selected.id}
          errors={props.itemErrors}
          onSelect={props.onSelect}
          onPatch={props.onPatch}
          onRemove={removeSelected}
          onLoadQuestCompletions={props.onLoadQuestCompletions}
        />
      )}
      {selected.kind === 'credential' && (
        <CredentialEditor
          key={selected.id}
          workspace={workspace}
          credentialId={selected.id}
          errors={props.itemErrors}
          generatedCredentialApiKeys={props.generatedCredentialApiKeys}
          onSelect={props.onSelect}
          onPatch={props.onPatch}
          onRemove={removeSelected}
        />
      )}
      {selected.kind === 'selection' && (
        <SelectionEditor
          workspace={workspace}
          selectionPreview={workspace.selectionPreview}
          errors={props.itemErrors}
          onPatchSelection={props.onPatchSelection}
          onRunSelectionPreview={props.onRunSelectionPreview}
          onRevealAllocations={props.onRevealAllocations}
          isRunningSelectionPreview={props.isRunningSelectionPreview}
          isRevealingAllocations={props.isRevealingAllocations}
        />
      )}
    </div>
  );
}
